/**
 * Data layer Impersonation read-only "View as user" (US-AE11.F3-0039-S03 · @UC9102). Endpoints:
 *   POST   /v1/admin/users/:id/impersonate  → ImpersonationSession (+token) — SUPER_ADMIN/OPS.
 *   GET    /v1/admin/impersonation/active    → { session } (banner render) — lazy expiry server-side.
 *   DELETE /v1/admin/impersonation/:sid      → ImpersonationSession (thoát/ended).
 *
 * Raw fetch + Bearer admin token (mirror features/disputes/api.ts). Shape DTO = MỘT NGUỒN SỰ THẬT
 * @goppy/contracts. KHÔNG log token/PII. G2: KHÔNG endpoint sửa tiền — phiên chỉ theo dõi read-only.
 */
import type {
  ActiveImpersonationResponse,
  ImpersonationSession,
  MeResponse,
  StartImpersonationBody,
} from "@goppy/contracts";
import { env } from "@/lib/env.js";
import { authedFetch, getToken } from "@/lib/auth.js";

/** Lỗi nghiệp vụ impersonation: `code` ổn định + `status` HTTP (400/403/404/409/5xx). */
class ImpersonationApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "ImpersonationApiError";
    this.code = code;
    this.status = status;
  }
}

interface ProblemDetail {
  code?: string;
  detail?: string;
  message?: string;
  title?: string;
}

function safeMessage(status: number, problem: ProblemDetail): string {
  if (status === 401) return "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.";
  if (status === 403) return "Bạn không có quyền thực hiện thao tác này.";
  if (status >= 500) return "Hệ thống đang gặp sự cố. Vui lòng thử lại sau.";
  return problem.detail ?? problem.message ?? problem.title ?? "Đã xảy ra lỗi. Vui lòng thử lại.";
}

async function toApiError(res: Response): Promise<ImpersonationApiError> {
  let problem: ProblemDetail = {};
  try {
    problem = (await res.json()) as ProblemDetail;
  } catch {
    // body rỗng / không JSON → giữ mặc định theo status.
  }
  const code = problem.code ?? `HTTP_${res.status}`;
  return new ImpersonationApiError(code, safeMessage(res.status, problem), res.status);
}

function authHeaders(): HeadersInit {
  const token = getToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

/** POST bắt đầu phiên "xem như user" (reason+ticketId). Server ghi audit nặng. mutating. */
export async function startImpersonation(
  userId: string,
  body: StartImpersonationBody,
): Promise<ImpersonationSession> {
  const res = await fetch(
    `${env.apiBaseUrl}/admin/users/${encodeURIComponent(userId)}/impersonate`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...authHeaders(), "content-type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as ImpersonationSession;
}

/**
 * GET phiên active hiện tại (banner). Server lazy-expire khi quá TTL → trả session null.
 * Poll 30s suốt phiên ⇒ đi qua authedFetch để token hết hạn được refresh + retry, KHÔNG rơi 401.
 */
export async function getActiveImpersonation(): Promise<ActiveImpersonationResponse> {
  const res = await authedFetch("/admin/impersonation/active", {
    method: "GET",
    credentials: "include",
  });
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as ActiveImpersonationResponse;
}

/**
 * Hồ sơ user (chỉ đọc) mà panel "xem như user" hiển thị — tối thiểu đủ chứng minh token hoạt động
 * end-to-end. Shape lấy từ GET /v1/me (backend user-scoped). Chỉ field không nhạy cảm.
 */
export interface ImpersonatedUserProfile {
  readonly id: string;
  readonly fullName: string | null;
  readonly email: string | null;
  readonly onboardingCompleted: boolean;
}

/**
 * GET /v1/me BẰNG token impersonation (Bearer) — đọc đúng màn hình của target user (read-only). CHỨNG
 * MINH token hoạt động end-to-end: backend UserAuthGuard nạp userActor=targetUser, ReadOnlyImpersonationGuard
 * cho qua GET (chặn mọi mutating). KHÔNG dùng admin token ở đây (phải là token user-scoped).
 *
 * Shape khớp MeResponse (@goppy/contracts). Chỉ hiển thị field không nhạy cảm.
 */
export async function getImpersonatedProfile(
  impersonationToken: string,
): Promise<ImpersonatedUserProfile> {
  const res = await fetch(`${env.apiBaseUrl}/me`, {
    method: "GET",
    credentials: "include",
    headers: { authorization: `Bearer ${impersonationToken}` },
  });
  if (!res.ok) throw await toApiError(res);
  const raw = (await res.json()) as MeResponse;
  return {
    id: raw.id,
    fullName: raw.fullName,
    email: raw.email,
    onboardingCompleted: raw.onboardingCompleted,
  };
}

/** DELETE kết thúc phiên (thoát). Server ghi audit end (duration). */
export async function endImpersonation(sessionId: string): Promise<ImpersonationSession> {
  const res = await fetch(
    `${env.apiBaseUrl}/admin/impersonation/${encodeURIComponent(sessionId)}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: { ...authHeaders() },
    },
  );
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as ImpersonationSession;
}
